<script setup lang="ts">
import modelsTradutor from "@/tradutors/models";

import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide, Navigation } from "vue3-carousel";

const props = defineProps({
  posts: {
    type: Object,
    required: true,
  },
  cardRouteName: {
    type: String,
    default: "language-modelos-name-video",
  },
  seeAllRouteName: {
    type: String,
    default: "language-videos",
  },
  seeAllLabel: {
    type: String,
    default: "",
  },
  sectionTitle: {
    type: String,
    default: "",
  },
  seeAllPrefix: {
    type: String,
    default: "",
  },
  seeAllHighlight: {
    type: String,
    default: "",
  },
  darkBackground: {
    type: Boolean,
    default: false,
  },
});
const route = useRoute();
const locale = computed(() => route.params.language);
const DESKTOP_MIN = 970;
const revealed = reactive(new Set<number>());

const translate = computed(() => {
  if (locale.value === "pt-br") {
    return modelsTradutor.portugues;
  } else {
    return modelsTradutor.ingles;
  }
});

const settings = ref({
  itemsToShow: 1.15,
  itemsToScroll: 1,
  snapAlign: "start" as const,
});

const breakpoints = ref({
  600: {
    itemsToShow: 2.05,
    snapAlign: "center" as const,
  },
  800: {
    itemsToShow: 3.05,
    snapAlign: "center" as const,
  },
});

const carouselRef = ref<any>(null);
const currentSlide = ref(0);

const totalSlides = computed(() => {
  const items = props.posts as unknown;
  if (Array.isArray(items)) return items.length;
  if (items && typeof items === "object") return Object.keys(items).length;
  return 0;
});

// Mostra no máximo 3 dots numa janela deslizante centrada no slide atual
// (imita o comportamento dynamicBullets do carrossel de blogs).
const MAX_DOTS = 3;
const dotWindow = computed(() => {
  const total = totalSlides.value;
  if (total <= MAX_DOTS) {
    return Array.from({ length: total }, (_, i) => i);
  }
  let start = currentSlide.value - 1;
  if (start < 0) start = 0;
  if (start > total - MAX_DOTS) start = total - MAX_DOTS;
  return Array.from({ length: MAX_DOTS }, (_, i) => start + i);
});

function goToSlide(index: number) {
  carouselRef.value?.slideTo(index);
}

function coverVideo(e: Event): HTMLVideoElement | null {
  return (e.currentTarget as HTMLElement).querySelector(".preview") as HTMLVideoElement | null;
}

// hover só existe no desktop (≥970); o guard neutraliza mouseenter sintético no touch
function playVideo(e: MouseEvent) {
  if (window.innerWidth >= DESKTOP_MIN) coverVideo(e)?.play().catch(() => {});
}
function pauseVideo(e: MouseEvent) {
  if (window.innerWidth >= DESKTOP_MIN) coverVideo(e)?.pause();
}

function onCoverClick(
  event: MouseEvent,
  navigate: (e?: MouseEvent) => void,
  id: number,
  hasPreview: boolean
) {
  // desktop: clicar em qualquer parte navega (SPA). navigate() faz preventDefault
  // no clique normal e respeita ctrl/cmd/clique-do-meio (abre a URL real via href).
  if (window.innerWidth >= DESKTOP_MIN) return navigate(event);
  // sem vídeo, ou vídeo já revelado → navega
  if (!hasPreview || revealed.has(id)) return navigate(event);
  // 1º toque: pausa/reseta os outros vídeos do carrossel e revela só este (1 por vez)
  event.preventDefault();
  const target = event.currentTarget as HTMLElement;
  pauseSiblingVideos(target);
  revealed.clear();
  revealed.add(id);
  target.querySelector<HTMLVideoElement>(".preview")?.play().catch(() => {});
}

// pausa e reseta todos os vídeos do mesmo grid/carrossel (garante 1 vídeo por vez)
function pauseSiblingVideos(clicked: HTMLElement) {
  const container = clicked.closest(".row, .carousel");
  container?.querySelectorAll<HTMLVideoElement>(".preview").forEach((v) => {
    v.pause();
    if (v.currentTime) v.currentTime = 0;
  });
}

// limpa o estado revelado se a seção troca de posts
watch(() => props.posts, () => revealed.clear());

const router = useRouter();

function redirect(name: string) {
  router.push({ name, params: { language: locale.value } });
}

</script>
<template>
  <div class="container" :class="{ 'dark-bg': props.darkBackground }">
    <h2 v-if="props.sectionTitle" class="section-title">{{ props.sectionTitle }}</h2>
    <Carousel
      ref="carouselRef"
      v-model="currentSlide"
      v-bind="settings"
      :breakpoints="breakpoints"
    >
      <Slide v-for="post in posts" :key="post.id">
        <div class="card pb-4">
          <NuxtLink
            :to="{name: props.cardRouteName, params: {name: post.slug}}"
            custom
            v-slot="{ href, navigate }"
          >
            <a
              :href="href"
              class="cover-link"
              :aria-label="post.title"
              @click="onCoverClick($event, navigate, post.id, !!post.preview)"
            >
              <div
                class="img-box"
                :class="{ 'is-playing': revealed.has(post.id) }"
                @mouseenter="playVideo"
                @mouseleave="pauseVideo"
              >
                <img
                  :src="`${post.cover}`"
                  :alt="post.imageAltText ?? post.title"
                  width="390"
                  height="305"
                  :class="{ 'imagem-estatica': post.preview }"
                />
                <video
                  v-if="post.preview"
                  preload="none"
                  playsinline
                  muted
                  loop
                  class="preview"
                  alt="Preview"
                  width="390"
                  height="305"
                >
                  <source :src="post.preview" type="video/mp4" />
                </video>
              </div>
            </a>
          </NuxtLink>
          <div class="card-body d-flex flex-column align-items-start">
            <NuxtLink :to="{name: props.cardRouteName, params: {name: post.slug}}" class="button--more">
              <span class="card-title">{{ post.title }}</span>
            </NuxtLink>
          </div>
        </div>
      </Slide>
      <!-- Setas (desktop) e dots (mobile) vão no HTML e o CSS escolhe (< 770):
           com v-if no isMobile(), false no servidor, a hidratação divergia -->
      <template #addons>
        <Navigation />
      </template>
    </Carousel>
    <div
      v-if="totalSlides > 1"
      class="carousel-dots"
    >
      <button
        v-for="index in dotWindow"
        :key="index"
        type="button"
        class="carousel-dots__dot"
        :class="{ 'carousel-dots__dot--active': index === currentSlide }"
        :aria-label="`Slide ${index + 1}`"
        @click="goToSlide(index)"
      />
    </div>
    <div class="row d-flex align-items-center justify-content-center">
      <button
        type="button"
        class="more-blog mt-4 mb-5 p-3 col-lg-5 col-11"
        @click="redirect(props.seeAllRouteName)"
      >
        <template v-if="props.seeAllHighlight">
          {{ props.seeAllPrefix }} <strong class="see-all-highlight">{{ props.seeAllHighlight }}</strong>
        </template>
        <template v-else>
          {{ props.seeAllLabel || translate.seeAllModels }}
        </template>
        <font-awesome :icon="['fas', 'arrow-right']" class="more-blog__arrow" />
      </button>
    </div>
  </div>
</template>
<style scoped lang="scss">
.section-title {
  display: none;
}
.see-all-highlight {
  font-weight: 700;
}
.card-title {
  color: #b20000;
  font-family: "Roboto-Medium";
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 22px;
}

.button--more svg {
  color: #b20000;
}

.button--more {
  height: 19px;
  color: #5b5858;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
}

.small svg {
  margin-right: 5px;
}

.card img {
  width: 390px;
  height: 305px !important;
  object-fit: cover;
}

.card {
  background-color: transparent;
  padding-right: 15px;
}

.card-body {
  padding: 10px 0 0;
}
.carousel-dots {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0px 0 10px;
}

.carousel-dots__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: #ffffff;
  opacity: 0.4;
  cursor: pointer;
  transition: width 0.3s ease, height 0.3s ease, opacity 0.3s ease;
}

.carousel-dots__dot--active {
  opacity: 1;
  width: 10px;
  height: 10px;
}

.more-blog {
  font-family: "Exo-2";
  font-size: 16px !important;
  font-weight: 400;
  background-color: black !important;
  border: 1px solid #ffffff !important;
  border-radius: 30px !important;
  color: white;
}

.more-blog svg {
  color: #b20;
  margin-left: 8px;
}

/* .img-box é o contêiner posicionado (relative) que serve de referência para o
   .preview sobreposto (absolute). Mantê-lo posicionado também evita que um filho
   absoluto escape até o .carousel__slide (que tem transform). */
.img-box {
  position: relative;
}
/* imagem estática em fluxo: define a área/altura do card */
.imagem-estatica {
  position: relative;
}
/* preview sobreposto: ocupa exatamente o mesmo espaço da imagem estática,
   evitando o "pulo" de layout que havia quando o vídeo ficava em fluxo com
   altura própria. object-fit: cover mantém a proporção sem distorcer. */
.preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  opacity: 0; /* Inicialmente invisível */
  transition: opacity 0.3s; /* Transição suave */
  z-index: 2;
}
/* reveal por toque (<970): a classe só é adicionada quando innerWidth < 970,
   por isso a regra fica no base (cobre também 770–969) */
.is-playing .preview {
  opacity: 1;
}
.is-playing .imagem-estatica {
  opacity: 0;
}

.cover-link {
  display: block;
  text-decoration: none;
}

/* reveal por hover só no desktop (≥970) */
@media screen and (min-width: 970px) {
  .img-box:hover .preview {
    opacity: 1; /* Torna o Preview visível ao passar o mouse */
  }
  .img-box:hover .imagem-estatica {
    opacity: 0; /* Torna a imagem estática invisível ao passar o mouse */
  }
}

/* regras desktop: título visível à esquerda e cores escuras */
@media screen and (min-width: 770px) {
  .section-title {
    display: block;
    text-align: left;
    color: #ffffff;
    font-family: "Bebas-Neue-Light";
    font-size: 64px;
    font-weight: 300;
    line-height: 1.1;
    letter-spacing: 0;
    text-transform: uppercase;
    margin: 0 0 16px;
    padding-top: 8px; /* padding (não margin) p/ evitar linha branca por margin-collapse */
    padding-left: 12px; /* alinhar ao início dos cards */
  }
  .container.dark-bg .card-title {
    color: #ffffff;
  }
  .container.dark-bg .button--more {
    color: #ffffff;
  }

  /* setas de navegação: a cor padrão do vue3-carousel é preta e some no
     fundo escuro; deixamos brancas e com um disco semitransparente p/ contraste */
  :deep(.carousel__prev),
  :deep(.carousel__next) {
    width: 44px;
    height: 44px;
    color: #ffffff;
    transition: background 0.3s ease;
    inset-block-start: 43%;
  }
  :deep(.carousel__icon) {
    width: 34px;
    height: 34px;
  }
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  :deep(.carousel__prev),
  :deep(.carousel__next) {
    display: none;
  }
  .carousel-dots {
    display: flex;
  }
  .more-blog__arrow {
    display: none;
  }
  .section-title {
    display: block;
    text-align: center;
    color: #ffffff;
    font-family: "Bebas-Neue-Light";
    font-size: 48px;
    line-height: 1.1;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin: 0 0 16px;
    padding-top: 8px;
  }
  .container.dark-bg .card-title {
    color: #ffffff;
  }
  .container.dark-bg .button--more {
    color: #ffffff;
  }
  .container.dark-bg .more-blog {
    background-color: #000 !important;
    border: 1px solid #ffffff !important;
    color: #ffffff;
  }

  .card {
    /* o .card é flex item do .carousel__slide (justify-content:center). Sem
       largura explícita ele encolhe para a largura intrínseca do conteúdo em
       fluxo (o <video>, já que a <img> é position:absolute). Vídeos diferentes
       => cards de larguras diferentes => imagens mais estreitas e vãos
       desiguais entre slides (sintoma visto nas lives). width:100% faz o card
       preencher o slide sempre, igualando os dois carrosséis. */
    width: 100%;
    padding-right: 12px;
  }
  .more-blog {
    width: 260px !important;
  }

  /* imagem estática em fluxo: define a altura do card (244px) */
  .imagem-estatica,
  .card img {
    position: relative;
    width: 100% !important;
    height: auto !important;
    object-fit: cover;
  }

  /* preview sobreposto ocupa exatamente o mesmo espaço da imagem estática */
  .preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }
  :deep(.carousel__viewport) {
    width: 104% !important;
  }
}

/* === Tablet/iPad (770–1199px): imagens fluidas + título proporcional === */
@media screen and (min-width: 770px) and (max-width: 1199px) {
  .section-title {
    font-size: 44px;
  }
  /* mesmo motivo do mobile: o card precisa preencher o slide para a imagem não
     ficar mais estreita que o slide (vão desigual entre os cards). */
  .card {
    width: 100%;
  }
  /* imagem estática em fluxo, altura proporcional à largura; define a altura da
     img-box. O .preview NÃO entra aqui de propósito: ele herda o overlay do base
     (.preview absolute, 100%×100%, object-fit:cover) e cobre exatamente a imagem.
     Com height:auto o vídeo (absoluto) assumiria a própria proporção intrínseca e
     ficaria menor que a imagem. */
  .card img,
  .imagem-estatica {
    width: 100% !important;
    height: auto !important;
    object-fit: cover !important;
  }
}
</style>
